import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export const EASE = [0.22, 1, 0.36, 1];

export const stagger = (s = 0.12, d = 0) => ({
  hidden: {},
  show: { transition: { staggerChildren: s, delayChildren: d } },
});

export function Reveal({ children, className, delay = 0, y = 30 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Line-by-line masked text reveal.
 * Each line sits in an overflow-hidden wrapper; inner text slides from below.
 */
export function MaskLines({
  lines,
  className = "",
  lineClassName = "",
  delay = 0,
  animateOnLoad = false,
}) {
  const trigger = animateOnLoad
    ? { animate: "show" }
    : { whileInView: "show", viewport: { once: true, margin: "-60px" } };

  return (
    <motion.span
      className={className}
      initial="hidden"
      {...trigger}
      variants={stagger(0.11, delay)}
    >
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.12em]">
          <motion.span
            className={`block ${lineClassName}`}
            variants={{
              hidden: { y: "115%" },
              show: { y: "0%", transition: { duration: 0.95, ease: EASE } },
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}

export function Chapter({ index, label, className = "" }) {
  return (
    <Reveal className={`flex items-center gap-4 ${className}`}>
      <span className="font-body text-xs font-semibold tracking-[0.3em] text-steel-light">
        {index}
      </span>
      <span className="h-px w-12 bg-hairline" />
      <span className="font-body text-xs font-medium uppercase tracking-[0.3em] text-fog">
        {label}
      </span>
    </Reveal>
  );
}

export function ArrowLink({ href, children, testid, primary = false }) {
  const base =
    "group inline-flex items-center gap-2.5 font-body text-sm font-semibold tracking-tight transition-colors duration-300";
  const styles = primary
    ? "bg-steel text-white hover:bg-steel-light px-7 py-3.5 rounded-sm"
    : "text-cloud hover:text-steel-light border-b border-hairline hover:border-steel-light pb-1";

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`${base} ${styles}`}
    >
      {children}
      <ArrowUpRight
        size={17}
        strokeWidth={2}
        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </a>
  );
}
